import { useCallback } from 'react'
import { useNotebookStore } from '@datalayer/jupyter-react'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  KbdGroup,
  Kbd,
  CmdCtrl,
  cn,
} from '@duck/ui'
import {
  Play,
  SkipForward,
  SquarePlus,
  Code,
  Type,
  ChevronUp,
  ChevronDown,
  Copy,
  Plus,
  Database,
} from 'lucide-react'
import type { CellType } from '@jupyterlab/nbformat'
import { useTar } from '@/modules/i18n'
import { trackNotebook } from '@/modules/notebookAnalytics'
import { HoldToDeleteButton } from './HoldToDeleteButton'
import { InjectDataPopover } from './InjectDataPopover'
import {
  PILL_BUTTON_ORDER,
  PILL_GROUP_SEPARATORS,
  type PillButtonId,
} from './notebookPillConfig'
import { useCellOverlayContext } from './cellOverlayContext'
import type { BadgeState, TableBadgeInfo } from './useNotebookTables'

const PILL_BUTTON =
  'inline-flex h-6 w-7 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-black/5 hover:text-foreground dark:hover:bg-white/5 dark:hover:text-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed'

interface LiveProps {
  mode: 'live'
  notebookId: string
  cellIndex: number
  cellType: CellType
  cellId: string
  isLastCell: boolean
  challengeId?: string
  isIdle: boolean
  enabled: readonly PillButtonId[]
  tables: TableBadgeInfo[]
  badgeStates: Record<string, BadgeState>
  onDownloadTable: (table: TableBadgeInfo) => Promise<boolean>
  onInsertReference: (tableName: string, cellIndex: number) => void
}

interface PreviewProps {
  mode: 'preview'
  enabled: readonly PillButtonId[]
}

export type PillButtonsProps = LiveProps | PreviewProps

export const PillButtons = (props: PillButtonsProps) => {
  const { t } = useTar()
  const notebookStore = useNotebookStore()
  const { notifyOpenChange } = useCellOverlayContext()
  const isLive = props.mode === 'live'

  // Live-only helper: focus this cell, then run an action targeting the active cell
  const withActiveCell = useCallback(
    <T,>(fn: () => T): T | undefined => {
      if (!isLive) return undefined
      const p = props as LiveProps
      const panel = notebookStore.selectNotebook(p.notebookId)?.adapter?.panel
      if (!panel) return undefined
      panel.content.activeCellIndex = p.cellIndex
      return fn()
    },
    [isLive, notebookStore, props],
  )

  const handleRun = useCallback(() => {
    if (!isLive) return
    const p = props as LiveProps
    withActiveCell(() => {
      const adapter = notebookStore.selectNotebook(p.notebookId)?.adapter
      adapter?.commands.execute('duck:notebook:run-and-stay-focused')
      trackNotebook('notebook:toolbar:run', { challengeId: p.challengeId })
    })
  }, [isLive, props, notebookStore, withActiveCell])

  const handleRunAdvance = useCallback(() => {
    if (!isLive) return
    const p = props as LiveProps
    withActiveCell(() => {
      notebookStore.run(p.notebookId)
      trackNotebook('notebook:toolbar:run', { challengeId: p.challengeId })
    })
  }, [isLive, props, notebookStore, withActiveCell])

  const handleRunInsert = useCallback(() => {
    if (!isLive) return
    const p = props as LiveProps
    withActiveCell(() => {
      const adapter = notebookStore.selectNotebook(p.notebookId)?.adapter
      adapter?.commands.execute('duck:notebook:run-and-insert-below')
      trackNotebook('notebook:toolbar:run', { challengeId: p.challengeId })
    })
  }, [isLive, props, notebookStore, withActiveCell])

  const handleToggleType = useCallback(() => {
    if (!isLive) return
    const p = props as LiveProps
    const target: CellType = p.cellType === 'markdown' ? 'code' : 'markdown'
    withActiveCell(() => {
      notebookStore.changeCellType(p.notebookId, target)
      trackNotebook('notebook:toolbar:cell_type_change', { challengeId: p.challengeId, newType: target })
    })
  }, [isLive, props, notebookStore, withActiveCell])

  const handleMoveUp = useCallback(() => {
    if (!isLive) return
    const p = props as LiveProps
    withActiveCell(() => {
      const panel = notebookStore.selectNotebook(p.notebookId)?.adapter?.panel
      if (panel && p.cellIndex > 0) {
        panel.content.moveCell(p.cellIndex, p.cellIndex - 1)
        trackNotebook('notebook:toolbar:move_cell', { challengeId: p.challengeId, direction: 'up' })
      }
    })
  }, [isLive, props, notebookStore, withActiveCell])

  const handleMoveDown = useCallback(() => {
    if (!isLive) return
    const p = props as LiveProps
    withActiveCell(() => {
      const panel = notebookStore.selectNotebook(p.notebookId)?.adapter?.panel
      if (panel && p.cellIndex < panel.content.widgets.length - 1) {
        panel.content.moveCell(p.cellIndex, p.cellIndex + 1)
        trackNotebook('notebook:toolbar:move_cell', { challengeId: p.challengeId, direction: 'down' })
      }
    })
  }, [isLive, props, notebookStore, withActiveCell])

  const handleDuplicate = useCallback(() => {
    if (!isLive) return
    const p = props as LiveProps
    withActiveCell(() => {
      const panel = notebookStore.selectNotebook(p.notebookId)?.adapter?.panel
      if (!panel) return
      const sourceModel = panel.content.widgets[p.cellIndex]?.model
      if (!sourceModel) return
      const sharedSource = sourceModel.sharedModel.toJSON()
      // Duplicate: preserve source + cell type, clear outputs and execution count
      const newCellJSON = { ...sharedSource, outputs: [], execution_count: null }
      const notebookModel = panel.content.model
      if (!notebookModel) return
      notebookModel.sharedModel.insertCell(p.cellIndex + 1, newCellJSON)
      trackNotebook('notebook:cell:duplicate', { challengeId: p.challengeId })
    })
  }, [isLive, props, notebookStore, withActiveCell])

  const handleAddBelow = useCallback(() => {
    if (!isLive) return
    const p = props as LiveProps
    withActiveCell(() => {
      notebookStore.insertBelow(p.notebookId, 'code')
      trackNotebook('notebook:toolbar:add_cell', { challengeId: p.challengeId, cellType: 'code' })
    })
  }, [isLive, props, notebookStore, withActiveCell])

  const handleDelete = useCallback(() => {
    if (!isLive) return
    const p = props as LiveProps
    withActiveCell(() => {
      notebookStore.delete(p.notebookId)
      trackNotebook('notebook:toolbar:delete_cell', { challengeId: p.challengeId })
    })
  }, [isLive, props, notebookStore, withActiveCell])

  // Read live-only flags safely (preview mode uses neutral defaults)
  const isIdle = isLive ? (props as LiveProps).isIdle : true
  const cellType: CellType = isLive ? (props as LiveProps).cellType : 'code'
  const cellIndex = isLive ? (props as LiveProps).cellIndex : 0
  const isLastCell = isLive ? (props as LiveProps).isLastCell : false

  const TypeIcon = cellType === 'markdown' ? Code : Type
  const typeTooltip =
    cellType === 'markdown'
      ? t('common.notebookCellSwitchToCode')
      : t('common.notebookCellSwitchToMarkdown')

  const renderButton = (id: PillButtonId): React.ReactNode => {
    if (!props.enabled.includes(id)) return null

    switch (id) {
      case 'run':
        return (
          <Tooltip key={id} onOpenChange={notifyOpenChange}>
            <TooltipTrigger asChild>
              <button
                type="button"
                disabled={!isIdle}
                className={cn(PILL_BUTTON, 'text-emerald-500 hover:text-emerald-400')}
                onClick={handleRun}
                aria-label={t('common.notebookCellRun')}
              >
                <Play className="h-3.5 w-3.5" fill="currentColor" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col items-center gap-1">
                <span>{t('common.notebookCellRun')}</span>
                <KbdGroup>
                  <CmdCtrl />
                  <Kbd>↵</Kbd>
                </KbdGroup>
              </div>
            </TooltipContent>
          </Tooltip>
        )
      case 'run-advance':
        return (
          <Tooltip key={id} onOpenChange={notifyOpenChange}>
            <TooltipTrigger asChild>
              <button
                type="button"
                disabled={!isIdle}
                className={cn(PILL_BUTTON, 'text-emerald-500 hover:text-emerald-400')}
                onClick={handleRunAdvance}
                aria-label={t('common.notebookCellRunAdvance')}
              >
                <SkipForward className="h-3.5 w-3.5" fill="currentColor" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col items-center gap-1">
                <span>{t('common.notebookCellRunAdvance')}</span>
                <KbdGroup>
                  <Kbd>Shift</Kbd>
                  <Kbd>↵</Kbd>
                </KbdGroup>
              </div>
            </TooltipContent>
          </Tooltip>
        )
      case 'run-insert':
        return (
          <Tooltip key={id} onOpenChange={notifyOpenChange}>
            <TooltipTrigger asChild>
              <button
                type="button"
                disabled={!isIdle}
                className={cn(PILL_BUTTON, 'text-emerald-500 hover:text-emerald-400')}
                onClick={handleRunInsert}
                aria-label={t('common.notebookCellRunInsert')}
              >
                <SquarePlus className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col items-center gap-1">
                <span>{t('common.notebookCellRunInsert')}</span>
                <KbdGroup>
                  <Kbd>Alt</Kbd>
                  <Kbd>↵</Kbd>
                </KbdGroup>
              </div>
            </TooltipContent>
          </Tooltip>
        )
      case 'type':
        return (
          <Tooltip key={id} onOpenChange={notifyOpenChange}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={PILL_BUTTON}
                onClick={handleToggleType}
                aria-label={typeTooltip}
              >
                <TypeIcon className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{typeTooltip}</TooltipContent>
          </Tooltip>
        )
      case 'move':
        // Single toggle controls both buttons — render them as a pair
        return (
          <span key={id} className="inline-flex">
            <Tooltip onOpenChange={notifyOpenChange}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  disabled={cellIndex === 0}
                  className={PILL_BUTTON}
                  onClick={handleMoveUp}
                  aria-label={t('common.notebookCellMoveUp')}
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('common.notebookCellMoveUp')}</TooltipContent>
            </Tooltip>
            <Tooltip onOpenChange={notifyOpenChange}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  disabled={isLastCell}
                  className={PILL_BUTTON}
                  onClick={handleMoveDown}
                  aria-label={t('common.notebookCellMoveDown')}
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('common.notebookCellMoveDown')}</TooltipContent>
            </Tooltip>
          </span>
        )
      case 'duplicate':
        return (
          <Tooltip key={id} onOpenChange={notifyOpenChange}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={PILL_BUTTON}
                onClick={handleDuplicate}
                aria-label={t('common.notebookCellDuplicate')}
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('common.notebookCellDuplicate')}</TooltipContent>
          </Tooltip>
        )
      case 'add-below':
        return (
          <Tooltip key={id} onOpenChange={notifyOpenChange}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={PILL_BUTTON}
                onClick={handleAddBelow}
                aria-label={t('common.notebookCellAddBelow')}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('common.notebookCellAddBelow')}</TooltipContent>
          </Tooltip>
        )
      case 'inject':
        if (!isLive) {
          // Preview: render the trigger only, no popover behavior
          return (
            <Tooltip key={id} onOpenChange={notifyOpenChange}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={PILL_BUTTON}
                  aria-label={t('common.notebookCellInjectData')}
                >
                  <Database className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('common.notebookCellInjectData')}</TooltipContent>
            </Tooltip>
          )
        }
        return (
          <InjectDataPopover
            key={id}
            cellIndex={(props as LiveProps).cellIndex}
            tables={(props as LiveProps).tables}
            badgeStates={(props as LiveProps).badgeStates}
            onInsert={(props as LiveProps).onInsertReference}
            onDownload={(props as LiveProps).onDownloadTable}
          />
        )
      case 'delete':
        return <HoldToDeleteButton key={id} onConfirm={handleDelete} />
    }
  }

  // Walk the canonical order, skipping disabled ids, inserting separators where appropriate.
  const items: React.ReactNode[] = []
  let lastWasVisible = false
  for (const id of PILL_BUTTON_ORDER) {
    if (!props.enabled.includes(id)) continue
    if (lastWasVisible && PILL_GROUP_SEPARATORS.includes(id)) {
      items.push(<span key={`sep-${id}`} className="mx-0.5 h-4 w-px bg-border" />)
    }
    items.push(renderButton(id))
    lastWasVisible = true
  }

  return <>{items}</>
}

export default PillButtons
